/**
 * Event-pages domain — hooks for fetching and creating EventSeries by BidRequest.
 *
 * @see components/Agreements/api/useEventSeries.ts for the Agreements domain
 * (listing, attaching, and managing EventSeries scoped to a PriceAgreement).
 */
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  createEventSeries,
  getEventSeries,
  getEventSeriesByBidRequest,
  terminateEventSeries,
} from '@api/EventSeriesApi';
import { CreateEventSeriesParams } from 'types/EventSeries';
import { ID } from 'types/ID';

export const EVENT_SERIES_QUERY_KEY = 'eventSeries';

export const useCreateEventSeries = () => {
  return useMutation({
    mutationFn: (params: CreateEventSeriesParams) => createEventSeries(params),
  });
};

export const useGetEventSeries = (id: ID | undefined) => {
  return useQuery({
    enabled: !!id,
    queryFn: async () => {
      const response = await getEventSeries(id!);

      return response.success ? response.data : null;
    },
    queryKey: [EVENT_SERIES_QUERY_KEY, 'detail', id],
  });
};

export const useGetEventSeriesByBidRequest = (
  bidRequestId: ID | undefined,
  { enabled = true }: { enabled?: boolean } = {}
) => {
  return useQuery({
    enabled: !!bidRequestId && enabled,
    queryFn: async () => {
      const response = await getEventSeriesByBidRequest(bidRequestId!);

      return response.success ? response.data : null;
    },
    queryKey: [EVENT_SERIES_QUERY_KEY, bidRequestId],
  });
};

/** Discontinues a series, so no further events are created for it. */
export const useTerminateEventSeries = () => {
  return useMutation({
    mutationFn: (id: ID) => terminateEventSeries(id),
  });
};
